import React, { createContext, useContext } from "react";
import { translations } from "./translations";

export const parseRecordDate = (rawDate) => {
  if (!rawDate) return { year: 2026, month: 4, day: 22 };
  const str = String(rawDate).trim();
  if (/^\d{8}$/.test(str)) {
    const year = parseInt(str.substring(0, 4), 10);
    const month = parseInt(str.substring(4, 6), 10);
    const day = parseInt(str.substring(6, 8), 10);
    return { year, month, day };
  }
  const match = str.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
  if (match) {
    return {
      year: parseInt(match[1], 10),
      month: parseInt(match[2], 10),
      day: parseInt(match[3], 10),
    };
  }
  const parsed = new Date(str);
  if (!isNaN(parsed.getTime())) {
    return {
      year: parsed.getFullYear(),
      month: parsed.getMonth() + 1,
      day: parsed.getDate(),
    };
  }
  return { year: 2026, month: 4, day: 22 };
};

const EN_MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const CN_MONTHS = [
  "一月",
  "二月",
  "三月",
  "四月",
  "五月",
  "六月",
  "七月",
  "八月",
  "九月",
  "十月",
  "十一月",
  "十二月",
];

const getOrdinal = (n) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

export const getTranslation = (lang, date, customUnits = {}) => {
  let base = translations[lang];
  if (!base) {
    if (
      lang === "yue-CN" ||
      lang === "tc-CN" ||
      lang === "tc" ||
      lang === "tw"
    ) {
      base = translations["tc-CN"] || translations["tc"];
    } else if (lang === "zn-CN" || lang === "cn" || lang === "zh") {
      base = translations["zn-CN"] || translations["cn"];
    } else {
      base = translations["en"];
    }
  }

  const { year, month, day } = parseRecordDate(date);
  const monthPadded = String(month).padStart(2, "0");
  const dayPadded = String(day).padStart(2, "0");
  const dateSlash = `${year}/${monthPadded}/${dayPadded}`;
  const enMonthName = EN_MONTHS[month - 1] || "April";
  const cnMonthName = CN_MONTHS[month - 1] || `${month}月`;
  const enDayOrdinal = getOrdinal(day);

  const isEn = lang === "en";
  const isCn = lang === "zn-CN" || lang === "cn" || lang === "zh";
  const isYue = lang === "yue-CN" || lang === "yue";
  const isTc = lang === "tc-CN" || lang === "tc" || lang === "tw";

  const dynamicMonthUnit = isEn ? enMonthName : cnMonthName;

  let dynamicHeader = base?.header;
  let dynamicExtractSource = base?.thirdFrame?.extractSource;

  if (isEn) {
    dynamicHeader = `The content of this video was extracted from the Daily Navigator published on ${day} ${enMonthName} ${year} and may have since been updated. For the most\n up-to-date idea, please refer to the latest edition of the Daily Navigator.`;
    dynamicExtractSource = [
      `The content of this video is extracted from the Standard Chartered Bank Daily navigator on ${day} ${enMonthName} ${year} and generated by Al. The copy right is owned solely by Standard Chartered Bank.`,
    ];
  } else if (isCn) {
    dynamicHeader = `本内容节选自${monthPadded}月${dayPadded}日(${dateSlash})市场领航日报,如需了解更多内容,以及后续观点更新,请参考市场领航日报正文`;
    dynamicExtractSource = [
      `本视频由AI生成,内容节选自${month}月${day}日市场领航日报,版权归属渣打银行所有。`,
    ];
  } else if (isYue) {
    dynamicHeader = `本內容摘錄自${dateSlash}市場領航日報，本文所涉及點位資訊參考，請參閱《市場領航日報》了解更多完整內容`;
    dynamicExtractSource = [
      `本影片由AI生成，內容摘錄自${month}月${day}日《市場領航日報》，版權歸渣打銀行所有。`,
    ];
  } else if (isTc) {
    dynamicHeader = `本內容摘錄自${dateSlash}市場領航日報,本文所涉及之點位資訊僅供參考,請參閱市場領航日報了解更多完整內容。`;
  }

  const firstUnit =
    customUnits?.firstUnit ||
    base?.secondFrame?.firstStage?.firstUnit ||
    "EUR";
  const secondUnit =
    customUnits?.secondUnit ||
    base?.secondFrame?.firstStage?.secondUnit ||
    "GBP";
  const viewLabel = customUnits?.viewLabel;

  const dynamicLine4 = isEn
    ? `${firstUnit}/${secondUnit}`
    : isCn
      ? `${firstUnit}兑${secondUnit}`
      : `${firstUnit}兌${secondUnit}`;

  const dynamicBullish = isEn
    ? `${viewLabel || "Bullish"} ${firstUnit} vs. ${secondUnit}`
    : isCn
      ? `${viewLabel || "看好"}${firstUnit}兑${secondUnit}`
      : `${viewLabel || "看好"}${firstUnit}兌${secondUnit}`;

  return {
    ...base,
    header: dynamicHeader,
    firstFrame: {
      ...base?.firstFrame,
      monthUnit: dynamicMonthUnit,
      day: day,
      line4: dynamicLine4,
    },
    secondFrame: {
      ...base?.secondFrame,
      firstStage: {
        ...base?.secondFrame?.firstStage,
        firstUnit,
        secondUnit,
      },
      thirdStage: {
        ...base?.secondFrame?.thirdStage,
        bullish: dynamicBullish,
      },
    },
    thirdFrame: {
      ...base?.thirdFrame,
      extractSource: dynamicExtractSource,
    },
  };
};

const LanguageContext = createContext({
  lang: "en",
  t: getTranslation("en"),
  date: "20260422",
  firstUnit: "",
  secondUnit: "",
  viewLabel: "",
});

export const LanguageProvider = ({
  lang = "en",
  date,
  firstUnit,
  secondUnit,
  viewLabel,
  children,
}) => {
  const t = getTranslation(lang, date, { firstUnit, secondUnit, viewLabel });
  return (
    <LanguageContext.Provider
      value={{ lang, t, date, firstUnit, secondUnit, viewLabel }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  return useContext(LanguageContext);
};
